<template>
  <ReviewItemDetails
    class="border-b border-l border-neutral-border-default"
    :expansion-panel="expansionPanel"
    :is-metadata-expanded="isMetadataExpanded"
    :review-item="reviewItem"
  />

  <div
    class="w-full py-5 px-4 items-center shrink-0 relative border-b border-l border-r border-mid-neutral-50"
  >
    <DuplicationButton
      :study="reviewItem"
      :is-loading="isLoading"
      @mark-as-duplicate="markStudyAsDuplicate"
      @mark-as-not-duplicate="markStudyAsNotDuplicate"
    />
  </div>
  <div
    class="w-full py-5 px-4 items-center shrink-0 relative border-b border-r border-mid-neutral-50"
  >
    <div class="flex gap-2.5 flex-col">
      <TitleAndAbstractScreeningButton
        v-if="
          review.entity.value?.plan?.screeningPlan?.titleAndAbstractCriteria
            .length ?? 0 > 0
        "
        :study="reviewItem"
        :is-loading="isLoading"
        @clear-title-and-abstract-screening="clearTitleAndAbstractScreening"
        @set-title-and-abstract-screening="setTitleAndAbstractScreening"
      />
      <FullTextScreeningButton
        :review-item="reviewItem"
        :is-loading="isLoading"
        @clear-full-text-screening="clearFullTextScreening"
        @set-full-text-screening="setFullTextScreening"
      />
    </div>
  </div>

  <div
    class="w-full py-5 px-4 items-center shrink-0 relative border-b border-mid-neutral-50"
  >
    <ImportCitation
      :review-item="reviewItem"
      :is-review-read-only="review.isReviewReadonly.value"
    />
    <div
      v-if="isCitationImported"
      class="absolute top-0 -right-7.5 w-8 h-full bg-yellow-200 flex flex-col justify-between items-center px-2"
    >
      <button class="flex items-center h-full" @click="deleteCitation">
        <svg
          class="w-6 text-yellow-800"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.667 4.66667L12.0888 12.7617C12.0389 13.4594 11.4584 14 10.7588 14H5.24182C4.5423 14 3.96171 13.4594 3.91187 12.7617L3.33366 4.66667M6.66699 7.33333V11.3333M9.33366 7.33333V11.3333M10.0003 4.66667V2.66667C10.0003 2.29848 9.70185 2 9.33366 2H6.66699C6.2988 2 6.00033 2.29848 6.00033 2.66667V4.66667M2.66699 4.66667H13.3337"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </div>
</template>
<script setup lang="ts">
import useExpansionPanel from '@app/composables/use-expansion-panel'
import { ReviewItem } from '@core/domain/models/reviewItem.model'
import { ref } from 'vue'
import useLoading from '@app/composables/use-loading'
import { injectStrict } from '@app/utils/injectStrict'
import useSnackbar from '@app/composables/use-snackbar'
import { SnackbarState } from '@app/types'
import { HttpException } from '@core/exceptions/http.exception'
import { errorMessage } from '@app/utils/error-message'
import { hideAll } from 'tippy.js'
import DuplicationButton from './DuplicationButton.vue'
import TitleAndAbstractScreeningButton from './TitleAndAbstractScreeningButton.vue'
import FullTextScreeningButton from './FullTextScreeningButton.vue'
import ImportCitation from './ImportCitation.vue'
import { ReviewKey } from '@app/views/Review/use-review'
import ReviewItemDetails from '@app/views/Review/Execute/ReviewItemDetails/ReviewItemDetails.vue'

const props = defineProps<{
  reviewItem: ReviewItem
  isMetadataExpanded: boolean
}>()
const expansionPanel = useExpansionPanel()

const isCitationImported = ref(false)
if (props.reviewItem.search.parentStudyId) {
  isCitationImported.value = true
}

const loading = useLoading()
const isLoading = ref(false)

const review = injectStrict(ReviewKey)

const snackbar = useSnackbar()

async function clearFullTextScreening() {
  try {
    startLoading()
    await review.clearStudyFullTextScreening(props.reviewItem.id)
    snackbar.success('Success')
    hideAll({ duration: 0 })
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    throw e
  } finally {
    stopLoading()
  }
}

function startLoading() {
  loading.start()
  isLoading.value = true
}
function stopLoading() {
  loading.stop()
  isLoading.value = false
}

async function markStudyAsDuplicate(parentStudyId: number) {
  try {
    startLoading()
    await review.markStudyAsDuplicate(props.reviewItem.id, parentStudyId)
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    stopLoading()
  }

  stopLoading()
}

async function markStudyAsNotDuplicate() {
  try {
    startLoading()
    await review.markStudyAsNotDuplicate(props.reviewItem.id)
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    throw e
  } finally {
    stopLoading()
  }
}

async function setTitleAndAbstractScreening(key: string) {
  if (
    !props.reviewItem.pdfFile &&
    review.entity.value?.plan?.screeningPlan?.titleAndAbstractCriteria
      ?.length === 0 &&
    key === 'Included'
  ) {
    snackbar.show(
      SnackbarState.ERROR,
      'You must upload the study to continue. Please upload a clean document without highlights or comments',
    )
    return
  }
  try {
    startLoading()
    await review.setStudyTitleAndAbstractScreening(props.reviewItem.id, key)
    snackbar.success('Success')
    hideAll({ duration: 0 })
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    throw e
  } finally {
    stopLoading()
  }
}

async function clearTitleAndAbstractScreening() {
  try {
    startLoading()
    await review.clearStudyTitleAndAbstractScreening(props.reviewItem.id)
    snackbar.success('Success')
    hideAll({ duration: 0 })
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    throw e
  } finally {
    stopLoading()
  }
}

async function setFullTextScreening(key: string) {
  try {
    startLoading()
    await review.setStudyFullTextScreening(props.reviewItem.id, key)
    snackbar.success('Success')
    hideAll({ duration: 0 })
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    throw e
  } finally {
    stopLoading()
  }
}

async function deleteCitation() {
  loading.start()
  try {
    const searchId = props.reviewItem?.search?.id
    if (!searchId) return
    await review.removeSearch(searchId)
  } finally {
    loading.stop()
  }
}
</script>
